import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BrokerLayout from '@/components/BrokerLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Building2, Users, FileText, TrendingUp, ArrowRight, ShieldAlert, CheckCircle2, Bell, Clock, AlertTriangle, Calendar, Phone, Send } from 'lucide-react';

const BrokerDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [reminders, setReminders] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [d, p, l, r] = await Promise.all([
        api.get('/broker/dashboard'),
        api.get('/broker/profile').catch(() => ({ data: null })),
        api.get('/broker/leads').catch(() => ({ data: [] })),
        api.get('/broker/reminders').catch(() => ({ data: null }))
      ]);
      setDashboard(d.data);
      setProfile(p.data);
      setLeads(l.data.slice(0, 5));
      setReminders(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  if (!dashboard) return <BrokerLayout><div>Loading...</div></BrokerLayout>;

  const stats = [
    { label: 'Total Projects', value: dashboard.total_projects, icon: Building2, color: 'text-blue-600 bg-blue-50' },
    { label: 'Active Projects', value: dashboard.active_projects, icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Landing Pages', value: dashboard.total_landing_pages, icon: FileText, color: 'text-purple-600 bg-purple-50' },
    { label: 'Total Leads', value: dashboard.total_leads, icon: Users, color: 'text-[#F97316] bg-orange-50' },
    { label: 'New Leads', value: dashboard.new_leads, icon: TrendingUp, color: 'text-red-600 bg-red-50' },
    { label: 'Qualified', value: dashboard.qualified_leads, icon: TrendingUp, color: 'text-amber-600 bg-amber-50' },
    { label: 'Booked', value: dashboard.booked_leads, icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Conversion %', value: `${dashboard.conversion_rate}%`, icon: TrendingUp, color: 'text-[#0F2C59] bg-slate-100' }
  ];

  return (
    <BrokerLayout profile={profile} remindersCount={reminders?.urgent_count || 0}>
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F2C59]" data-testid="broker-dashboard-title">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Welcome back — here's your business snapshot</p>
      </div>

      {/* KYC Alert */}
      {dashboard.profile_status !== 'approved' && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="kyc-alert">
          <div className="flex items-start gap-3">
            <ShieldAlert size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                {dashboard.profile_status === 'draft' && 'Complete Your KYC to Start'}
                {dashboard.profile_status === 'submitted' && 'KYC Under Review'}
                {dashboard.profile_status === 'rejected' && 'KYC Rejected — Update Required'}
              </div>
              <p className="text-sm text-amber-800 mt-1">
                {dashboard.profile_status === 'draft' && 'You need to complete your KYC and submit for admin approval before you can publish projects and generate landing pages.'}
                {dashboard.profile_status === 'submitted' && 'Your profile is being reviewed by our admin team. You will be notified once approved.'}
                {dashboard.profile_status === 'rejected' && 'Please review admin feedback and update your KYC.'}
              </p>
              <div className="mt-3">
                <div className="text-xs text-amber-800 mb-1 font-semibold">Profile Completion: {dashboard.profile_completion}%</div>
                <Progress value={dashboard.profile_completion} className="h-2" />
              </div>
              <Link to="/broker/kyc">
                <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700" data-testid="complete-kyc-btn">Complete KYC →</Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* FOLLOW-UP REMINDERS */}
      {reminders && (
        <div className="mb-6 rounded-lg border-2 border-[#F97316]/40 bg-gradient-to-br from-orange-50 to-white p-5" data-testid="reminders-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Bell size={20} className="text-[#F97316]" />
                {reminders.urgent_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
                    {reminders.urgent_count}
                  </span>
                )}
              </div>
              <h2 className="font-display font-bold text-lg text-[#0F2C59]">Daily Reminders</h2>
              <span className="text-xs text-slate-500 hidden sm:inline">— Email & WhatsApp digest at 9 AM & 7 PM IST</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await api.post('/broker/digest/test');
                    toast.success('Digest sent to your email' + (profile?.whatsapp_number ? ' & WhatsApp' : ''));
                  } catch (e) {
                    toast.error('Failed to send digest');
                  }
                }}
                data-testid="send-test-digest-btn"
                className="text-xs"
              >
                <Send size={12} className="mr-1"/> Test Digest
              </Button>
              <Link to="/broker/leads" className="text-xs font-semibold text-[#F97316] hover:underline" data-testid="view-all-reminders">View all →</Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Overdue */}
            <div className={`rounded-md border p-3 ${reminders.overdue?.length > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`} data-testid="reminder-overdue">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                <AlertTriangle size={14} className={reminders.overdue?.length > 0 ? 'text-red-600' : 'text-slate-400'} />
                <span className={reminders.overdue?.length > 0 ? 'text-red-700' : 'text-slate-500'}>Overdue</span>
              </div>
              <div className="font-display font-black text-2xl mt-1 text-slate-900">{reminders.overdue?.length || 0}</div>
              <div className="text-xs text-slate-500 mt-0.5">past follow-ups</div>
            </div>

            {/* Today */}
            <div className={`rounded-md border p-3 ${reminders.today?.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`} data-testid="reminder-today">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                <Clock size={14} className={reminders.today?.length > 0 ? 'text-amber-600' : 'text-slate-400'} />
                <span className={reminders.today?.length > 0 ? 'text-amber-700' : 'text-slate-500'}>Today</span>
              </div>
              <div className="font-display font-black text-2xl mt-1 text-slate-900">{reminders.today?.length || 0}</div>
              <div className="text-xs text-slate-500 mt-0.5">to follow up</div>
            </div>

            {/* Site Visits */}
            <div className={`rounded-md border p-3 ${reminders.site_visits_today?.length > 0 ? 'border-purple-200 bg-purple-50' : 'border-slate-200 bg-white'}`} data-testid="reminder-site-visits">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                <Calendar size={14} className={reminders.site_visits_today?.length > 0 ? 'text-purple-600' : 'text-slate-400'} />
                <span className={reminders.site_visits_today?.length > 0 ? 'text-purple-700' : 'text-slate-500'}>Site Visits</span>
              </div>
              <div className="font-display font-black text-2xl mt-1 text-slate-900">{reminders.site_visits_today?.length || 0}</div>
              <div className="text-xs text-slate-500 mt-0.5">scheduled today</div>
            </div>

            {/* Stale New */}
            <div className={`rounded-md border p-3 ${reminders.stale_new?.length > 0 ? 'border-orange-200 bg-orange-50' : 'border-slate-200 bg-white'}`} data-testid="reminder-stale-new">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                <Bell size={14} className={reminders.stale_new?.length > 0 ? 'text-[#F97316]' : 'text-slate-400'} />
                <span className={reminders.stale_new?.length > 0 ? 'text-orange-700' : 'text-slate-500'}>Untouched</span>
              </div>
              <div className="font-display font-black text-2xl mt-1 text-slate-900">{reminders.stale_new?.length || 0}</div>
              <div className="text-xs text-slate-500 mt-0.5">new &gt; 24h old</div>
            </div>
          </div>

          {/* Urgent Lead List */}
          {(reminders.overdue?.length > 0 || reminders.site_visits_today?.length > 0 || reminders.today?.length > 0) && (
            <div className="mt-4 space-y-2">
              {[...(reminders.overdue || []).map(l => ({...l, bucket: 'overdue'})),
                ...(reminders.site_visits_today || []).map(l => ({...l, bucket: 'site_visit'})),
                ...(reminders.today || []).map(l => ({...l, bucket: 'today'}))].slice(0, 5).map(lead => (
                <div key={lead.id + lead.bucket} className="flex items-center justify-between p-3 bg-white rounded-md border border-slate-200" data-testid={`reminder-lead-${lead.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${lead.bucket === 'overdue' ? 'bg-red-500' : lead.bucket === 'site_visit' ? 'bg-purple-500' : 'bg-amber-500'}`}/>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-slate-900 truncate">{lead.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {lead.bucket === 'overdue' && `Overdue since ${lead.follow_up_date}`}
                        {lead.bucket === 'site_visit' && `Site visit today`}
                        {lead.bucket === 'today' && `Follow-up today`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={`tel:${lead.mobile}`} className="text-blue-600 hover:text-[#0F2C59] p-1.5 hover:bg-blue-50 rounded" title="Call" data-testid={`reminder-call-${lead.id}`}><Phone size={14}/></a>
                    <Link to="/broker/leads" className="text-xs font-semibold text-[#F97316] hover:underline">Open</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(stat => (
          <div key={stat.label} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-').replace('%', '')}`}>
            <div className="flex items-center justify-between">
              <div className={`w-10 h-10 rounded-md ${stat.color} flex items-center justify-center`}>
                <stat.icon size={18} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{stat.label}</div>
              <div className="font-display font-extrabold text-2xl text-[#0F2C59] mt-1">{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { to: '/broker/projects', label: 'Add New Project', icon: Building2, color: 'bg-[#0F2C59] hover:bg-[#0F2C59]/90' },
          { to: '/broker/leads', label: 'View All Leads', icon: Users, color: 'bg-[#F97316] hover:bg-[#EA580C]' },
          { to: '/broker/landing-pages', label: 'Create Landing Page', icon: FileText, color: 'bg-emerald-600 hover:bg-emerald-700' }
        ].map(action => (
          <Link key={action.to} to={action.to}>
            <div className={`${action.color} text-white rounded-lg p-5 flex items-center justify-between cursor-pointer hover:-translate-y-0.5 transition-transform`} data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-3">
                <action.icon size={20} />
                <span className="font-semibold">{action.label}</span>
              </div>
              <ArrowRight size={18} />
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Leads */}
      <div className="mt-8 bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg text-[#0F2C59]">Recent Leads</h2>
          <Link to="/broker/leads" className="text-sm text-[#F97316] font-semibold hover:underline" data-testid="view-all-leads-link">View All</Link>
        </div>
        {leads.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500">No leads yet. Publish landing pages to start receiving enquiries.</div>
        ) : (
          <div className="space-y-2">
            {leads.map(lead => (
              <div key={lead.id} className="flex items-center justify-between p-3 rounded-md border border-slate-100 hover:border-slate-200" data-testid={`recent-lead-${lead.id}`}>
                <div>
                  <div className="font-semibold text-sm text-slate-900">{lead.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{lead.mobile} • {new Date(lead.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-md font-medium ${
                  lead.status === 'new' ? 'bg-blue-50 text-blue-700' :
                  lead.status === 'closed_won' || lead.status === 'booked' ? 'bg-emerald-50 text-emerald-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {lead.status?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </BrokerLayout>
  );
};

export default BrokerDashboard;
